/* eslint-disable react-native/no-inline-styles */
import React, {useEffect, useState} from 'react';
import {
  ScrollView,
  Text,
  View,
  StyleSheet,
  Switch,
  Alert,
  TouchableOpacity,
  Image,
} from 'react-native';
import Select from '../../../components/Select';
import Geolocation from '@react-native-community/geolocation';
import MapView, {Marker} from 'react-native-maps';
import {addWorkOrder, updateWorkOrder} from '../api';
import {useSelector} from 'react-redux';
import FormContainer from '../../../components/FormContainer';
import LoadingSpinner from '../../../components/LoadingSpinner';
import PhotoCaptureSheet from '../../../components/PhotoCaptureSheet';
import Button from '../../../components/Button';
import Icon from '@expo/vector-icons/Feather';
import Input from '../../../components/Input';

import {useRoute} from '@react-navigation/native';
import WorkOrderCostsTable from '../components/WorkOrderCostsTable';
import {
  showErrorMessage,
  showSuccessMessage,
} from '../../../service/flashMessage';
import {WorkOrderCost} from '../../../@types/WorkOrder';
import Container from '../../../components/Container';
import Navbar from '../../../components/Navbar';
import {
  INITIAL_REGION,
  LATITUDE_DELTA,
  LONGITUDE_DELTA,
} from '../constants';
import {RootStore} from '../../../redux/store';
import {fetchListBySite, getCreateState, getEditState} from '../utils';

export default function CreateWorkOrder({navigation}) {
  const currentUnitLink = useSelector((state: RootStore) => state.units.unitLinks).find(
    link => link.isDefault === true,
  );
  const siteId = currentUnitLink?.unit?.site?.id;
  const userId = useSelector((state: RootStore) => state.auth.user.id);
  const [maintenanceTypes, setMaintenanceTypes] = useState([]);
  const [assignedTos, setAssignedTos] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const {params} = useRoute();

  // @ts-ignore
  const maintenanceToEdit = params?.workOrder;

  const inEditMode = !!maintenanceToEdit;

  const editState = getEditState(inEditMode, maintenanceToEdit);

  const createState = getCreateState(siteId, userId);

  const [workOrder, setWorkOrder] = useState(
    inEditMode ? editState : createState,
  );
  const [region, setRegion] = useState(INITIAL_REGION);
  const [imageChooserShown, setImageChooserShown] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    initApiCalls();
  }, []);

  async function initApiCalls() {
    setIsLoading(true);
    if (!siteId) {
      setMaintenanceTypes([]);
      setAssignedTos([]);
      setStatuses([]);
      setIsLoading(false);
      return;
    }
    inEditMode ? getPointPosition() : getInitialPosition();
    await getListBySites();
    setIsLoading(false);
  }

  function getPointPosition() {
    setRegion({
      latitude: workOrder.latitude,
      longitude: workOrder.longitude,
      latitudeDelta: LATITUDE_DELTA,
      longitudeDelta: LONGITUDE_DELTA,
    });
  }

  function getInitialPosition() {
    Geolocation.getCurrentPosition(
      position => {
        setRegion({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          latitudeDelta: LATITUDE_DELTA,
          longitudeDelta: LONGITUDE_DELTA,
        });
        setWorkOrder({
          ...workOrder,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      error => showErrorMessage(error.message),
      {enableHighAccuracy: false},
    );
  }

  async function getListBySites() {
    try {
      const typesList = await fetchListBySite('Type', siteId, 'Maintenance');
      setMaintenanceTypes(typesList);
      const assignees = await fetchListBySite(
        'AssignedTo',
        siteId,
        'Maintenance',
      );
      setAssignedTos(assignees);
      const _statuses = await fetchListBySite('Status', siteId, 'Maintenance');
      setStatuses(_statuses);
    } catch (error) {
      showErrorMessage(error.message);
    }
  }

  async function createWorkOrder() {
    setSubmitting(true);
    try {
      if (!currentUnitLink?.unit?.id) {
        showErrorMessage('Please select a default unit first');
        setSubmitting(false);
        return;
      }
      const res = inEditMode
        ? await updateWorkOrder(maintenanceToEdit.id, workOrder)
        : await addWorkOrder({
            ...workOrder,
            unitId: currentUnitLink?.unit?.id,
            siteId,
            communityId: currentUnitLink?.unit?.community?.id,
            buildingId: currentUnitLink?.unit?.building?.id,
            siteName: currentUnitLink?.unit?.site?.name,
            communityName: currentUnitLink?.unit?.community?.name,
            buildingName: currentUnitLink?.unit?.building?.name,
            locationUnitDisplay: currentUnitLink?.unit?.unitDisplay,
          });
      showSuccessMessage('Work order saved successfully');
      setSubmitting(false);
      navigation.goBack();
    } catch (error) {
      setSubmitting(false);

      showErrorMessage(error.message);
    }
  }


  return (
    <Container>
      <Navbar
        navigation={navigation}
        title={inEditMode ? 'Edit Work Order' : 'Create Work Order'}
      />
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <FormContainer>
          {!siteId ? (
            <Text style={styles.my2}>No default unit is selected yet.</Text>
          ) : null}
          <ScrollView keyboardShouldPersistTaps="handled">
            <View style={styles.container}>
              <>
                <View style={styles.row}>
                  <Text style={styles.onePickerLabel}>Status</Text>
                  <Select
                    width={200}
                    placeholder="Select status"
                    selectedValue={workOrder.status}
                    isDisabled
                    >
                    {['Pending'].map(type => (
                      <Select.Item label={type} value={type} key={type} />
                    ))}
                  </Select>
                </View>
              </>
              <>
                <View style={styles.row}>
                  <Text style={styles.onePickerLabel}>Type</Text>
                  <Select
                    width={200}
                    placeholder="Select type"
                    selectedValue={workOrder.type}
                    onValueChange={value => {
                      setWorkOrder({...workOrder, type: value});
                    }}>
                    {maintenanceTypes.map(type => (
                      <Select.Item label={type} value={type} key={type} />
                    ))}
                  </Select>
                </View>
              </>
              <View style={styles.row}>
                <Text style={styles.switchLabel}>Location is a unit</Text>
                <Switch
                  value={workOrder.locationIsAunit}
                  onValueChange={value => {
                    setWorkOrder({...workOrder, locationIsAunit: value});
                  }}
                />
              </View>
              <>
                <Text style={styles.userNameLabel}>Location</Text>
                <Input
                  placeholder="Type location"
                  style={{height: 100, fontSize:18}}
                  value={workOrder.location}
                  onChangeText={text => {
                    setWorkOrder({...workOrder, location: text});
                  }}
                />
              </>
              <>
                <View>
                  <Text style={styles.my2}>Picture</Text>
                  <TouchableOpacity
                    onPress={() => setImageChooserShown(true)}
                    style={styles.pictureUploadContainer}>
                    {workOrder.pictureImage ? (
                      <Image
                        source={{
                          uri: `data:image/jpeg;base64,${workOrder.pictureImage}`,
                        }}
                        style={styles.vehicleImage}
                      />
                    ) : (
                      <Icon
                        name="image"
                        size={24}
                        style={styles.pictureUploadIcon}
                      />
                    )}
                  </TouchableOpacity>
                </View>
              </>
              <View style={{marginTop: 10}}>
                <Text style={styles.userNameLabel}>Short description</Text>
                <Input
                  placeholder="Type short description"
                  value={workOrder.shortDescription}
                  onChangeText={text => {
                    setWorkOrder({...workOrder, shortDescription: text});
                  }}
                />
              </View>
              <>
                <Text style={styles.userNameLabel}>Description</Text>
                <Input
                  placeholder="Type Description"
                  style={{height: 100, fontSize:18}}
                  value={workOrder.description}
                  onChangeText={text => {
                    setWorkOrder({...workOrder, description: text});
                  }}
                />
              </>
              <>
                <MapView
                  style={styles.map}
                  minZoomLevel={19}
                  showsUserLocation
                  region={region}>
                  <Marker
                    title="Marker"
                    description="Drag and drop to update location"
                    coordinate={{
                      latitude: region.latitude,
                      longitude: region.longitude,
                    }}
                    draggable
                    onDragEnd={e => {
                      setRegion({
                        ...region,
                        latitude: e.nativeEvent.coordinate.latitude,
                        longitude: e.nativeEvent.coordinate.longitude,
                      });
                      setWorkOrder({
                        ...workOrder,
                        latitude: e.nativeEvent.coordinate.latitude,
                        longitude: e.nativeEvent.coordinate.longitude,
                      });
                    }}
                  />
                </MapView>
                <Text style={styles.locationLabel}>
                  Longitude : {region.longitude}
                </Text>
                <Text style={styles.locationLabel}>
                  Latitude : {region.latitude}
                </Text>
              </>
            </View>
          </ScrollView>
          <PhotoCaptureSheet
            isPanelActive={imageChooserShown}
            setIsActive={(val: boolean) => setImageChooserShown(val)}
            onConfirmImage={(image: string) => {
              setWorkOrder({...workOrder, pictureImage: image});
            }}
          />
          {!isLoading && (
            <Button
              onPress={createWorkOrder}
              isLoading={submitting}
              isLoadingText="Saving ..."
              style={{margin: 16, marginBottom: 52}}>
              {inEditMode ? 'Update work order' : 'Save work order'}
            </Button>
          )}
        </FormContainer>
      )}
    </Container>
  );
}

const styles = StyleSheet.create({
  container: {
    marginTop: 8,
    paddingHorizontal: 16,
    marginBottom: 16,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
    alignItems: 'center',
  },
  my2: {
    marginVertical: 8,
  },
  onePickerLabel: {
    // color: TEXT_DEFAULT_COLOR,
    marginTop: 8,
    flex: 1,
    fontSize: 15,
  },
  userNameLabel: {
    // color: TEXT_DEFAULT_COLOR,
    marginBottom: 4,
    flex: 1,
    fontSize: 15,
  },
  userNameInput: {
    height: 40,
    // borderColor: TEXT_GRAY_COLOR,
    borderWidth: 1,
    borderRadius: 4,
    padding: 8,
    flex: 1,
    color: 'black',
    marginBottom: 16,
  },
  imageChooser: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
    marginBottom: 16,
  },
  imageContainer: {
    borderWidth: 1,
    borderColor: 'gray',
    borderRadius: 8,
    width: 128,
    height: 96,
    alignItems: 'center',
    justifyContent: 'center',
  },
  map: {
    width: '100%',
    height: 200,
    marginTop: 32,
  },
  locationLabel: {
    fontSize: 14,
    marginTop: 16,
    flex: 1,
  },
  switchLabel: {
    // color: TEXT_DEFAULT_COLOR,
    marginTop: 14,
    marginBottom: 4,
    flex: 1,
    fontSize: 15,
  },
  pictureUploadContainer: {
    borderWidth: 1,
    borderColor: '#cbd5e0',
    borderRadius: 4,
    width: 128,
    height: 96,
  },
  vehicleImage: {
    borderRadius: 8,
    width: 128,
    height: 96,
  },
  pictureUploadIcon: {
    marginTop: 36,
    marginStart: 36,
    color: '#4b5563',
  },
});
